/**
 * D17 — opening a `.chain` that Needs approval, by someone who can't approve it (or on a surface
 * with no Approve, the share view): it doesn't open — this says why, in the same glass dialog as
 * the Approve dialog. A person who CAN approve gets `ApprovePointerDialog` instead.
 */
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { FileIcon, fileIconProps, useTheme, getThemeClass } from '@papan/core-ui/ui'

export function PointerNeedsApprovalDialog({ fileName, isFolder = true, onClose }: {
  fileName: string
  /** The chain's target kind when the row knows it (a chain at a FILE lists as a file);
   *  defaults to a folder. */
  isFolder?: boolean
  onClose: () => void
}) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const okRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Focus moves INTO the dialog (its OK button) and goes back to the opener on close.
  // Escape / Enter belong to this dialog alone: they are taken in the CAPTURE phase on
  // window — before any document listener (the share overlay's Escape closes the whole
  // file) and before the opener's own default (Enter on the focused tree row re-clicks
  // it, re-opening this dialog).
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    okRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Enter') return
      e.preventDefault()
      e.stopPropagation()
      onCloseRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[14vh] pb-4" role="dialog" aria-modal="true" aria-label={t('driveView.chain.needsApprovalTitle')} data-testid="chain-needs-approval-dialog">
      <div className="absolute inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-md flex flex-col`}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-1">
          <div className="min-w-0">
            <h3 className="text-base font-semibold themed-text">{t('driveView.chain.needsApprovalTitle')}</h3>
            <p className="mt-1 text-xs leading-relaxed themed-text-muted">{t('driveView.chain.needsApprovalBody')}</p>
          </div>
          <button onClick={onClose} className="themed-icon hover:themed-text p-1 rounded ws-glass-well shrink-0" aria-label={t('driveView.chain.needsApprovalOk')}>
            <X size={16} />
          </button>
        </div>
        <div className="px-5 pt-4 pb-5">
          <div className="flex items-center gap-2.5 rounded-lg ws-glass-well px-3 py-2.5">
            <FileIcon {...fileIconProps({ name: fileName, isFolder, fileType: 'chain', chainNeedsApproval: true })} size={18} className="shrink-0" />
            <span className="text-sm font-medium themed-text truncate" title={fileName}>{fileName}</span>
          </div>
        </div>
        <div className="ws-glass-footer flex items-center justify-end gap-2 px-5 py-3">
          <button ref={okRef} type="button" onClick={onClose} className="px-4 py-1.5 text-sm themed-btn-primary rounded-lg" data-testid="chain-needs-approval-ok">
            {t('driveView.chain.needsApprovalOk')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
